import React, { useRef, useState } from "react";
import { Camera, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Small button that lets the user upload / replace / clear a player's headshot.
 * Shows as a floating pill or icon-only depending on `variant`.
 */
export default function HeadshotUploader({ playerId, hasUpload, onUpdated, variant = "compact" }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const pick = (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    fileRef.current?.click();
  };

  const onChange = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Pick an image file");
      return;
    }
    if (f.size > 8 * 1024 * 1024) {
      toast.error("Max 8MB");
      return;
    }
    setBusy(true);
    try {
      const form = new FormData();
      form.append("file", f);
      await axios.post(`${API}/players/${playerId}/upload-headshot`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Photo updated");
      onUpdated && onUpdated();
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || err.message });
    } finally {
      setBusy(false);
    }
  };

  const clear = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (!window.confirm("Remove uploaded photo?")) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/players/${playerId}/headshot`);
      toast.success("Photo removed");
      onUpdated && onUpdated();
    } catch (err) {
      toast.error("Remove failed");
    } finally {
      setBusy(false);
    }
  };

  const iconClass = variant === "large" ? "w-4 h-4" : "w-3.5 h-3.5";

  if (variant === "large") {
    return (
      <div className="flex items-center gap-2">
        <input ref={fileRef} type="file" accept="image/*" onChange={onChange} className="hidden" data-testid={`upload-input-${playerId}`} />
        <button
          onClick={pick}
          disabled={busy}
          className="flex items-center gap-2 border border-white/20 bg-[#1c1c1c]/80 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] disabled:opacity-50"
          style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
          data-testid={`upload-headshot-${playerId}`}
        >
          {busy ? <Loader2 className={`${iconClass} animate-spin`} /> : <Camera className={iconClass} />}
          {hasUpload ? "Replace Photo" : "Upload Photo"}
        </button>
        {hasUpload && (
          <button
            onClick={clear}
            disabled={busy}
            className="flex items-center gap-2 border border-white/20 bg-[#1c1c1c]/80 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.25em] text-[#a0a0a0] hover:border-red-500 hover:text-red-500 disabled:opacity-50"
            style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
            data-testid={`remove-headshot-${playerId}`}
          >
            <Trash2 className={iconClass} />
          </button>
        )}
      </div>
    );
  }

  // Compact overlay
  return (
    <>
      <input ref={fileRef} type="file" accept="image/*" onChange={onChange} className="hidden" data-testid={`upload-input-${playerId}`} />
      <button
        onClick={pick}
        disabled={busy}
        className="w-7 h-7 border border-white/20 bg-[#101010]/90 backdrop-blur flex items-center justify-center text-[#a0a0a0] hover:border-[var(--org-accent)] hover:text-[var(--org-accent)] disabled:opacity-50"
        style={{ transition: "border-color 0.2s ease, color 0.2s ease" }}
        title={hasUpload ? "Replace photo" : "Upload photo"}
        data-testid={`upload-headshot-${playerId}`}
      >
        {busy ? <Loader2 className={iconClass + " animate-spin"} /> : <Camera className={iconClass} />}
      </button>
    </>
  );
}
